import { isFunction } from '@agilepoint/utils-common';
import { Component, createRef } from 'react';
import styled, { css } from 'styled-components';
import { Callout } from '../callout';
import { PrimaryButton, SecondaryButton } from '../button';
import { IColorPickerCalloutProps } from './color-picker-callout-types';
import { ColorPicker as ColorPickerBase } from '../color-picker';
import { KeyStrokeListener } from '../../utils';
import { StackPanel } from '../stack-panel';

const actionButtonCommonStyle = css`
    align-self: end;
    margin: 0px 24px 8px 8px;
`;

const ColorPicker = styled(ColorPickerBase)`
    padding: 24px;
`;

const ClearActionButton = styled(SecondaryButton)`
    ${actionButtonCommonStyle};
`;

const ApplyActionButton = styled(PrimaryButton)`
    ${actionButtonCommonStyle};
`;

const StyledCallout = styled(Callout)`
    & > .ap-callout-content{
        display: flex;
        flex-flow: column;
    }
`;

type CalloutTargetProps = Pick<IColorPickerCalloutState, 'colorString' | 'pickerTargetElementWidth' | 'pickerTargetElementHeight'>;

const CalloutTarget = styled.div
    .attrs(({ colorString, pickerTargetElementWidth, pickerTargetElementHeight }: CalloutTargetProps) => ({
        style: {
            backgroundColor: colorString,
            width: pickerTargetElementWidth,
            height: pickerTargetElementHeight
        },
        className: 'ap-colorPickerCallout-defaultButton'
    })) <CalloutTargetProps>`
    display: inline-block;
    cursor: pointer;
    outline-color: ${({ theme: { semantics: { palette } } }) => palette.themePrimary};
    border-style: solid;
    box-shadow: ${({ theme: { colorPickerCallout: { composites } } }) => composites.boxShadow};
    border-width: ${({ theme: { colorPickerCallout: { composites } } }) => composites.borderWidth}; 
    border-color: ${({ theme: { colorPickerCallout: { colors } } }) => colors.borderColor};
    border-radius: ${({ theme: { colorPickerCallout: { composites } } }) => composites.borderRadius};
`;

const Wrapper = styled.div.attrs({
    className: String('ap-color-picker-callout')
})`
    display: inline-block;
`;

interface IColorPickerCalloutState {
    isCalloutHidden?: boolean;
    colorString: string;
    pickerTargetElementWidth?: number;
    pickerTargetElementHeight?: number;
}

export class ColorPickerCallout extends Component<IColorPickerCalloutProps, IColorPickerCalloutState> {
    private readonly targetRef;
    private keyStrokeListener: KeyStrokeListener;
    private prevSelectedColor?: string;
    private isDirty?: boolean;

    static getDerivedStateFromProps(props: IColorPickerCalloutProps, oldState: IColorPickerCalloutState) {
        if (props.color === undefined || props.color === oldState.colorString) {
            return null;
        }
        return {
            colorString: props.color
        };
    }

    constructor(props: IColorPickerCalloutProps) {
        super(props);
        this.targetRef = props.elementRef ?? createRef<HTMLDivElement>();
        this.keyStrokeListener = new KeyStrokeListener(this.targetRef);
        this.state = {
            colorString: props.color! || props.defaultColor!,
            isCalloutHidden: true
        };
    }

    componentDidMount() {
        this.keyStrokeListener.subscribe(this.toggleCallout, 'Enter');
        this.keyStrokeListener.subscribe(this.onCalloutDismiss, 'Escape');
    }

    componentWillUnmount() {
        this.keyStrokeListener.unsubscribe();
    }

    render() {
        const { showClear, className, showApply } = this.props;
        const { isCalloutHidden } = this.state;
        return (
            <>
                <Wrapper ref={this.targetRef} className={className}>
                    {this.renderTriggerButton()}
                </Wrapper>
                <StyledCallout hidden={isCalloutHidden} targetRef={this.targetRef} onDismiss={this.onCalloutDismiss} isBeakVisible preventClickEventPropagation closeOnEscape autofocusOnOpen>
                    {this.renderColorPicker()}
                    <StackPanel hidden={!(showApply || showClear)} orientation="horizontal" gap="8" justifyContent="flex-end">
                        <>
                            {showClear && this.renderClearActionButton()}
                            {showApply && this.renderApplyActionButton()}
                        </>
                    </StackPanel>
                </StyledCallout>
            </>
        );
    }

    private updateState = (stateUpdateCallback: (state: IColorPickerCalloutState) => Partial<IColorPickerCalloutState>, callback?: () => void) => {
        this.setState(oldState => ({ ...oldState, ...stateUpdateCallback(oldState) }), callback);
    }

    private toggleCallout = () => {
        const { colorString } = this.state;
        this.prevSelectedColor = colorString;
        this.updateState(oldState => ({ isCalloutHidden: !oldState.isCalloutHidden }));
    }

    private onCalloutDismiss = () => {
        const { showApply } = this.props;
        if (showApply && this.prevSelectedColor !== undefined && this.isDirty) {
            this.setColorStringInState(this.prevSelectedColor);
        }
        this.setState({ isCalloutHidden: true });
    }

    private setColorStringInState(color: string) {
        this.updateState(() => ({ colorString: color }), () => {
            const { onColorChange } = this.props;
            onColorChange?.(color);
        });
    }

    private onClearClick = () => {
        const { onClear } = this.props;
        const { colorString } = this.state;
        onClear?.(this.prevSelectedColor ?? colorString);
    }

    private onValueChange = (color: string) => {
        this.isDirty = true;
        this.setColorStringInState(color);
    }

    private renderColorPicker() {
        const { opacityEnabled, pickerSurfaceWidth, pickerSurfaceHeight } = this.props;
        const { colorString } = this.state;
        return (
            <ColorPicker opacityEnabled={opacityEnabled} value={colorString} onValueChange={this.onValueChange}
                pickerSurfaceWidth={pickerSurfaceWidth} pickerSurfaceHeight={pickerSurfaceHeight} />
        );
    }

    private renderClearActionButton() {
        const { localization = { clearButtonLabel: 'Clear' } } = this.props;
        return (
            <ClearActionButton label={localization.clearButtonLabel} onClick={this.onClearClick} />
        );
    }

    private onApplyClick = () => {
        const { colorString } = this.state;
        this.isDirty = false;
        this.setState({
            isCalloutHidden: true
        });
        this.setColorStringInState(colorString);
    }

    private renderApplyActionButton() {
        const { localization = { applyButtonLabel: 'Apply' } } = this.props;
        return (
            <ApplyActionButton label={localization.applyButtonLabel} onClick={this.onApplyClick} />
        );
    }

    private renderTriggerButton() {
        const { onRender } = this.props;
        const { colorString } = this.state;
        const { pickerTargetElementWidth = 24, pickerTargetElementHeight = 24 } = this.props;

        if (isFunction(onRender)) {
            return onRender({
                toggleCallout: this.toggleCallout,
                color: colorString
            });
        }

        return (
            <CalloutTarget
                onClick={this.toggleCallout}
                colorString={colorString}
                pickerTargetElementWidth={pickerTargetElementWidth}
                pickerTargetElementHeight={pickerTargetElementHeight}
                tabIndex={0} />
        );
    }
}
